import React, { useState, useEffect, useRef } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import "./KDModleeModelSelection.css";

const ModleeModelSelection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [report, setReport] = useState("");
  const [tableData, setTableData] = useState([]);
  const [modelArchitecture, setModelArchitecture] = useState("");
  const [selectedExperiment, setSelectedExperiment] = useState("text_classification_wandb.zip");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null); // Store local URL reference

  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // Handle File Upload (Store & Create URL Reference)
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/zip") {
      setUploadedFile(file);
      setFileUrl(URL.createObjectURL(file)); // Create a local object URL for preview/reference
    } else {
      alert("Please upload a valid ZIP file.");
    }
  };

  // Simulated API call to prove referencing capability
  const fetchConversionReport = () => {
    const lastUsed = uploadedFile ? `Uploaded File: ${uploadedFile.name}` : `Selected Experiment: ${selectedExperiment}`;
    
    console.log("Stored file reference for API:", uploadedFile);

    const dummyData = {
      report: `This is a summary of the model selection process based on past experiments. Last used: ${lastUsed}`,
      tableData: [
        { type: "Type A", x: 0.8, y: 0.75 },
        { type: "Type A", x: 0.85, y: 0.78 },
        { type: "Type B", x: 0.9, y: 0.82 },
        { type: "Type B", x: 0.7, y: 0.6 },
        { type: "Type C", x: 0.95, y: 0.88 },
        { type: "Type C", x: 0.6, y: 0.55 }
      ],
      modelArchitecture: `
class Model(nn.Module):
    def __init__(self):
        super(Model, self).__init__()
        self.fc1 = nn.Linear(128, 64)
        self.fc2 = nn.Linear(64, 10)

    def forward(self, x):
        x = F.relu(self.fc1(x))
        return self.fc2(x)
      `
    };

    setReport(dummyData.report);
    setTableData(dummyData.tableData);
    setModelArchitecture(dummyData.modelArchitecture);
  };

  // Group data by type for color coding
  const groupedData = {
    "Type A": tableData.filter(d => d.type === "Type A"),
    "Type B": tableData.filter(d => d.type === "Type B"),
    "Type C": tableData.filter(d => d.type === "Type C"),
  };

  // Define colors for each type
  const colorMapping = {
    "Type A": "#FF5733",
    "Type B": "#33FF57",
    "Type C": "#3380FF"
  };

  return (
    <div ref={containerRef} className={`modlee-container ${isVisible ? "visible" : ""}`}>
      <h2 className="modlee-title">Test Out Model Selection</h2>
      <p className="modlee-description">Explore how Modlee learns from past experiments to optimize model selection for new datasets.</p>

      <div className="modlee-section">
        <h3 className="column-title">Experiment Overview</h3>
        <img src={`${process.env.PUBLIC_URL}/experimentation.svg`} alt="Experiment Anatomy" className="agent-image" />
        <p className="modlee-description">
          Reproducible deep learning requires consistent documentation of data, models, and performance. However, inconsistencies in documentation make large-scale insights impossible. 
          At Modlee, we standardize experiment tracking to enable data-driven model selection and meta-learning.
        </p>
      </div>

      <h3 className="column-title">Select Experiments</h3>

      <div className="modlee-section two-column">
        <div className="modlee-column">
          <h4>Curated Experiments</h4>
          <select 
            className="modlee-dropdown" 
            onChange={(e) => setSelectedExperiment(e.target.value)}
            value={selectedExperiment}
          >
            <option value="text_classification_wandb.zip">Text Classification - Weights and Biases</option>
          </select>
          <p className="modlee-description-column">
            Deep learning experiments documented using non-Modlee MLOps tools on various open-source datasets.
          </p>
        </div>
        {/* <div className="modlee-column">
          <h4>Upload Your Own Experiments</h4>
          <input type="file" accept=".zip" onChange={handleFileUpload} />
          {uploadedFile && (
            <p className="modlee-description-column">
              Uploaded File: <strong>{uploadedFile.name}</strong> 
              {fileUrl && (
                <>
                  {" "}
                  | <a href={fileUrl} target="_blank" rel="noopener noreferrer">Preview</a>
                </>
              )}
            </p>
          )}
        </div> */}

        <div className="modlee-column">
          <h4>Upload Your Own Experiments</h4>
          <input type="file" accept=".zip" onChange={handleFileUpload} />
          
          {/* Keep the existing text */}
          <p className="modlee-description-column">
            Please use our {" "}
            <a 
              href={`${process.env.PUBLIC_URL}/convert_text_classification_wandb_to_modlee.py`} 
              download 
              className="modlee-link"
            >
              recommended python script
            </a> {" "}
            to prepare Weights & Biases experiments.
          </p>

          {/* Show uploaded file details below without overwriting */}
          {uploadedFile && (
            <p className="modlee-description-column">
              <strong>Uploaded File:</strong> {uploadedFile.name} 
              {fileUrl && (
                <>
                  {" "} | <a href={fileUrl} target="_blank" rel="noopener noreferrer">Preview</a>
                </>
              )}
            </p>
          )}
        </div>



      </div>

      <h2>Model Selection</h2>

      {/* Button for API Call */}
      <div className="modlee-section">
        <button 
          className="modlee-button" 
          onClick={fetchConversionReport}
          disabled={!selectedExperiment && !uploadedFile} 
        >
          Analyze Selected Experiments
        </button>
      </div>

      {/* Conversion Report */}
      <div className="modlee-section">
        <h4>Conversion Report</h4>
        <textarea className="modlee-textbox" value={report} readOnly></textarea>
      </div>

      {/* Scatter Plot */}
      <div className="modlee-section two-column">
        <div className="modlee-column modlee-chart">
          <h4>Experiment Visualization</h4>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid />
              <XAxis type="number" dataKey="x" name="X Value" tick={{ fill: 'transparent' }} />
              <YAxis type="number" dataKey="y" name="Y Value" tick={{ fill: 'transparent' }} />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} />
              <Legend />
              {Object.keys(groupedData).map((type, index) => (
                <Scatter key={index} name={type} data={groupedData[type]} fill={colorMapping[type]} />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="modlee-column">
          <h4>Recommended Model</h4>
          <pre className="modlee-code"><code>{modelArchitecture}</code></pre>
        </div>
      </div>
    </div>
  );
};

export default ModleeModelSelection;
